'use client';

import { c as _c } from "react/compiler-runtime";
import { formatGermanNumber } from '@utils/utils-format';
import type { ProductReviewSummary, ProductReviews } from 'src/lib/trusted-shops/product-reviews';
import { RatingBreakdown } from 'src/shared/composites/trusted-shops/rating-breakdown';
import { RatingStars } from 'src/shared/composites/trusted-shops/rating-stars';
import { RatingValue } from 'src/shared/composites/trusted-shops/rating-value';
import { TrustedLogo } from 'src/shared/composites/trusted-shops/trusted-logo';
type ExperienceReviewSummaryData = ProductReviewSummary & Partial<Pick<ProductReviews, 'distribution'>>;
const RATING_LABELS = ['', 'Mangelhaft', 'Ausreichend', 'Befriedigend', 'Gut', 'Sehr gut'];
export function ExperienceReviewSummary(t0) {
  const $ = _c(27);
  const {
    data
  } = t0;
  const ratingLabel = RATING_LABELS[Math.round(data.rating)] ?? "";
  let t1;
  if ($[0] !== data.rating) {
    t1 = <RatingValue rating={data.rating} className="font-display" />;
    $[0] = data.rating;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="text-secondary-text">/ 5</span>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1) {
    t3 = <div className="flex items-baseline gap-1.5">{t1}{t2}</div>;
    $[3] = t1;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== data.rating) {
    t4 = <RatingStars rating={data.rating} size="md" />;
    $[5] = data.rating;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3 || $[8] !== t4) {
    t5 = <div className="flex flex-col gap-2">{t3}{t4}</div>;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== ratingLabel) {
    t6 = <p className="font-heading4">{ratingLabel}</p>;
    $[10] = ratingLabel;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const t7 = `Basiert auf ${data.count === 1 ? "einer Bewertung" : `${formatGermanNumber(data.count)} Bewertungen`}`;
  let t8;
  if ($[12] !== t7) {
    t8 = <p className="font-body-small text-secondary-text">{t7}</p>;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t6 || $[15] !== t8) {
    t9 = <div className="pt-1">{t6}{t8}</div>;
    $[14] = t6;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t5 || $[18] !== t9) {
    t10 = <div className="flex gap-4 sm:flex-col">{t5}{t9}</div>;
    $[17] = t5;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== data.count || $[21] !== data.distribution) {
    t11 = data.distribution && <RatingBreakdown breakdown={data.distribution} totalReviews={data.count} variant="single-star" />;
    $[20] = data.count;
    $[21] = data.distribution;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div className="flex items-start gap-2 pt-2 font-base-caption"><TrustedLogo className="mt-0.5 shrink-0" /><TrustedReviewsLinks /></div>;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t10 || $[25] !== t11) {
    t13 = <div className="flex h-full flex-col gap-4 rounded-2xl bg-sand p-6 sm:gap-6">{t10}{t11}{t12}</div>;
    $[24] = t10;
    $[25] = t11;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  return t13;
}

/** Flat rating bar: score and label on the left, the Trusted Shops note on the right (wraps below when narrow). */
export function ExperienceReviewSummaryBar(t0) {
  const $ = _c(23);
  const {
    data
  } = t0;
  const ratingLabel = RATING_LABELS[Math.round(data.rating)] ?? "";
  let t1;
  if ($[0] !== data.rating) {
    t1 = <RatingValue rating={data.rating} className="font-heading2" />;
    $[0] = data.rating;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="font-body-small text-secondary-text">/ 5</span>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1) {
    t3 = <div className="flex items-baseline gap-1.5">{t1}{t2}</div>;
    $[3] = t1;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== data.rating) {
    t4 = <RatingStars rating={data.rating} size="md" />;
    $[5] = data.rating;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3 || $[8] !== t4) {
    t5 = <div className="flex flex-col">{t3}{t4}</div>;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== ratingLabel) {
    t6 = <p className="font-heading4-small">{ratingLabel}</p>;
    $[10] = ratingLabel;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const t7 = `Basiert auf ${data.count === 1 ? "einer Bewertung" : `${formatGermanNumber(data.count)} Bewertungen`}`;
  let t8;
  if ($[12] !== t7) {
    t8 = <p className="font-body-small text-secondary-text">{t7}</p>;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t6 || $[15] !== t8) {
    t9 = <div>{t6}{t8}</div>;
    $[14] = t6;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t5 || $[18] !== t9) {
    t10 = <div className="flex items-center gap-4">{t5}{t9}</div>;
    $[17] = t5;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="flex max-w-60 items-start gap-2 font-base-caption text-secondary-text"><TrustedLogo width={16} height={16} className="shrink-0" /><TrustedReviewsLinks /></div>;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t10) {
    t12 = <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-4 rounded-lg bg-sand px-4 py-5">{t10}{t11}</div>;
    $[21] = t10;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  return t12;
}
function TrustedReviewsLinks() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <p>{"Gesammelt unter den Trusted Shops "}<a href="https://www.trustedshops.de/hubfs/legal-documents/consumer-membership-terms/de/consumer_membership_terms_de_20260618.pdf" target="_blank" rel="noreferrer" className="underline underline-offset-2 hover:no-underline">Nutzungsbedingungen</a><br /><a href="https://help.etrusted.com/hc/de/articles/23970864566162-Informationen-zum-Trusted-Shops-G%C3%BCtesiegel-und-zu-den-Trusted-Shops-Bewertungen" target="_blank" rel="noreferrer" className="underline underline-offset-2 hover:no-underline">Über die Echtheit der Trusted Shops Bewertungen.</a></p>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}